// Copyright (c) 2021 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { catchError, filter, mergeMap } from "rxjs/operators"
import { StatusCode } from "grpc-web"
import { Action } from "redux"
import { DetachTagRequest, DetachTagResponse, IdList } from "generated/wisetime/user/timeline/v4/timeline_pb"
import * as Actions from "../actions"
import * as TagsActions from "features/tags/actions"
import { grpc } from "app/rpc/executor"
import { timelineClient } from "app/rpc/client"
import * as TimelineSlice from "../slices/timeline"
import { isGrpcError } from "../../../lib/rpcutils"
import { t } from "@lingui/macro"
import { showSnackBar } from "../../snackbar/actions"

export type DetachTagRpc = (request: DetachTagRequest) => Observable<DetachTagResponse>

export const detachTagFulfilled = (detachTagRequest: DetachTagRequest): Observable<Action> =>
  detachTagRequest.hasTimeRowIds() ? detachTagFromRows(detachTagRequest) : detachTagFromGroup(detachTagRequest)

export const detachTag = (detachTagRpc: DetachTagRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.detachTag.match),
      mergeMap(({ payload: detachTagRequest }) =>
        of(detachTagRequest)
          .pipe(
            mergeMap(detachTagRpc),
            mergeMap(_ => detachTagFulfilled(detachTagRequest)),
            catchError(handleError),
          )
      ),
    )

export const detachTagRpc = (request: DetachTagRequest): Observable<DetachTagResponse> =>
  grpc(() => timelineClient.detachTag(request, null))

const detachTagFromRows = (request: DetachTagRequest): Observable<Action> =>
  concat(
    of(TimelineSlice.detachTimeRowsTag({
      timeRowIds: request.getTimeRowIds() ?? new IdList().setIdsList([]),
      tagId: request.getTagId()
    })),
    of(TagsActions.fetchRecentTags())
  )

const detachTagFromGroup = (request: DetachTagRequest): Observable<Action> =>
  concat(
    of(TimelineSlice.detachTimeGroupTag({
      timeGroupId: request.getTimeGroupId(),
      tagId: request.getTagId()
    })),
    of(TagsActions.fetchRecentTags())
  )

const handleError = (e: unknown): Observable<Action> => {
  if (isGrpcError(e)) {
    const { code } = e
    if (code === StatusCode.NOT_FOUND) {
      return of(showSnackBar({
        message: t({ id: "msg_detach_tag_failed_not_found", message: "Failed to detach tag. Could not find the target time row or time group." }),
        messageType: "ERROR",
      }))
    }
    if (code === StatusCode.PERMISSION_DENIED) {
      return of(showSnackBar({
        message: t({ id: "msg_detach_tag_failed_permission_denied", message: "Failed to detach tag. You do not have permission to add a tag to the time row or time group." }),
        messageType: "ERROR",
      }))
    }
    if (code === StatusCode.FAILED_PRECONDITION) {
      return concat(
        of(showSnackBar({
          message: t({ id: "msg_detach_tag_failed_precondition", message: "Failed to detach tag. Time row may be out of sync." }),
          messageType: "ERROR"
        })),
        of(Actions.onTimelineOutOfSync())
      )
    }
  }
  return of(showSnackBar({
    message: t({ id: "msg_detach_tag_failed", message: "Failed to detach tag. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
    messageType: "ERROR",
  }))
}

export default detachTag(detachTagRpc)
